import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { db } from '@/lib/supabaseQuery';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { useLocalizations } from '@/hooks/useLocalizations';
import { queryKeys } from '@/lib/queryKeys';

export function useFavoriteUsers() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { lPages } = useLocalizations();

  const { data: favorites, isLoading } = useQuery({
    queryKey: queryKeys.favorites.experts(user?.id),
    queryFn: async () => {
      if (!user?.id) return [];

      const data = await db.fetchAll<{ entity_id: string; created_at: string }>('users_favorites', 'entity_id, created_at', {
        filters: [
          { column: 'user_id', operator: 'eq', value: user.id },
          { column: 'entity_type', operator: 'eq', value: 'expert' },
        ],
        orderBy: { column: 'created_at', ascending: false }
      });

      return data || [];
    },
    enabled: !!user?.id,
  });

  const isFavorite = (userId: string) => {
    return favorites?.some((fav) => fav.entity_id === userId) || false;
  };

  const toggleFavorite = useMutation({
    mutationFn: async (userId: string) => {
      if (!user?.id) throw new Error('Must be logged in');

      const isFav = isFavorite(userId);

      if (isFav) {
        await db.delete('users_favorites', [
          { column: 'user_id', operator: 'eq', value: user.id },
          { column: 'entity_type', operator: 'eq', value: 'expert' },
          { column: 'entity_id', operator: 'eq', value: userId }
        ]);

        return { action: 'removed' as const, userId };
      } else {
        await db.insert('users_favorites', { user_id: user.id, entity_type: 'expert', entity_id: userId });

        return { action: 'added' as const, userId };
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.favorites.experts(user?.id) });
      
      if (data.action === 'added') {
        toast.success(lPages('favorites.success.expert_added', { defaultValue: 'Expert added to favorites' }));
      } else {
        toast.success(lPages('favorites.success.expert_removed', { defaultValue: 'Expert removed from favorites' }));
      }
    },
    onError: () => {
      toast.error(lPages('favorites.error.update_failed', { defaultValue: 'Failed to update favorites' }));
    },
  });

  return {
    favorites,
    isLoading,
    isFavorite,
    toggleFavorite: toggleFavorite.mutate,
    isToggling: toggleFavorite.isPending,
  };
}
